import type { JSX } from 'react';
import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Input,
  Modal,
  Select,
  Space,
  Tag,
  Typography,
} from 'antd';
import { useMutation } from '@tanstack/react-query';

import { vkBotApi, type LlmProviderStatus, type TestReplyResult } from '../lib/vkBotApi';

/**
 * «Тест бота»: прогоняет комментарий через реальный пайплайн (LLM +
 * гео-проверка), но БЕЗ публикации в ВК. Можно выбрать провайдера —
 * удобно сравнивать GigaChat / Claude / ChatGPT бок-о-бок.
 */
export function TestReplyModal({
  open,
  groupId,
  providers,
  onClose,
}: {
  open: boolean;
  groupId: string;
  providers: LlmProviderStatus[];
  onClose: () => void;
}): JSX.Element {
  const [postText, setPostText] = useState('');
  const [commentText, setCommentText] = useState('');
  const [authorName, setAuthorName] = useState('');
  const [provider, setProvider] = useState<string | undefined>(undefined);
  const [results, setResults] = useState<Array<{ label: string; r: TestReplyResult }>>([]);

  const runMut = useMutation({
    mutationFn: async () => {
      const r = await vkBotApi.test(groupId, {
        postText: postText.trim() || undefined,
        commentText: commentText.trim(),
        authorName: authorName.trim() || undefined,
        provider: provider as 'gigachat' | 'anthropic' | 'openai' | undefined,
      });
      return [{ label: r.provider, r }];
    },
    onSuccess: (rs) => setResults(rs),
  });

  const runAllMut = useMutation({
    mutationFn: async () => {
      const enabled = providers.filter((p) => p.enabled);
      const out: Array<{ label: string; r: TestReplyResult }> = [];
      for (const p of enabled) {
        const r = await vkBotApi.test(groupId, {
          postText: postText.trim() || undefined,
          commentText: commentText.trim(),
          authorName: authorName.trim() || undefined,
          provider: p.provider,
        });
        out.push({ label: p.provider, r });
      }
      return out;
    },
    onSuccess: (rs) => setResults(rs),
  });

  const busy = runMut.isPending || runAllMut.isPending;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="🧪 Тест бота (без публикации в ВК)"
      width={720}
      footer={null}
      destroyOnHidden
    >
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Input.TextArea
          rows={2}
          placeholder="Текст поста (например: «В какой класс пойдут ваши дети? Пишите в комментариях!»)"
          value={postText}
          onChange={(e) => setPostText(e.target.value)}
        />
        <Input.TextArea
          rows={2}
          placeholder="Комментарий клиента (например: «А на Ленина 15 доставите?»)"
          value={commentText}
          onChange={(e) => setCommentText(e.target.value)}
        />
        <Space wrap>
          <Input
            placeholder="Имя клиента (опц.)"
            value={authorName}
            onChange={(e) => setAuthorName(e.target.value)}
            style={{ width: 200 }}
          />
          <Select
            placeholder="Провайдер (из настроек)"
            allowClear
            style={{ width: 220 }}
            value={provider}
            onChange={setProvider}
            options={providers.map((p) => ({
              value: p.provider,
              label: p.provider + (p.enabled ? '' : ' (недоступен)'),
              disabled: !p.enabled,
            }))}
          />
          <Button
            type="primary"
            loading={runMut.isPending}
            disabled={!commentText.trim() || busy}
            onClick={() => runMut.mutate()}
          >
            Прогнать
          </Button>
          <Button
            loading={runAllMut.isPending}
            disabled={!commentText.trim() || busy}
            onClick={() => runAllMut.mutate()}
          >
            Сравнить все доступные
          </Button>
        </Space>

        {(runMut.error || runAllMut.error) && (
          <Alert
            type="error"
            showIcon
            message={String(
              (runMut.error ?? runAllMut.error) instanceof Error
                ? ((runMut.error ?? runAllMut.error) as Error).message
                : 'Ошибка теста',
            )}
          />
        )}

        {results.map(({ label, r }) => (
          <Card key={label} size="small" title={
            <Space size={8}>
              <Tag color="blue">{label}</Tag>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {(r.tookMs / 1000).toFixed(1)} с
              </Typography.Text>
            </Space>
          }>
            {r.replyText ? (
              <Typography.Paragraph style={{ marginBottom: 8, fontSize: 15 }}>
                💬 {r.replyText}
              </Typography.Paragraph>
            ) : (
              <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
                Бот решил промолчать{r.skipReason ? ` (${r.skipReason})` : ''}
              </Typography.Paragraph>
            )}
            {r.geoResult != null && (
              <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                Гео: {JSON.stringify(r.geoResult)}
              </Typography.Text>
            )}
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Решение LLM: {JSON.stringify(r.decision)}
            </Typography.Text>
          </Card>
        ))}
      </Space>
    </Modal>
  );
}
